/* eslint-disable react/prop-types */
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Container, Typography, Grid, Button, Stack, InputBase,
  Paper, Chip, CircularProgress, Alert,
} from '@mui/material';
import { Search as SearchIcon, ArrowForward as ArrowIcon, School, Route, Star } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCatalog } from '../../context/CatalogContext';
import ContentCard from '../../components/common/ContentCard';

const QUICK_TOPICS = [
  { label: 'Azure', filter: 'azure' },
  { label: 'AI & ML', filter: 'ai-machine-learning' },
  { label: 'Security', filter: 'security' },
  { label: 'Data', filter: 'data-ai' },
  { label: 'Developer', filter: 'developer' },
  { label: 'Power Platform', filter: 'power-platform' },
  { label: 'Microsoft 365', filter: 'microsoft-365' },
];

export default function HomePage() {
  const navigate = useNavigate();
  const { modules, learningPaths, loading, error, loadModules, loadLearningPaths } = useCatalog();
  const [heroSearch, setHeroSearch] = useState('');

  useEffect(() => {
    if (modules.length === 0) loadModules();
    if (learningPaths.length === 0) loadLearningPaths();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const featured = useMemo(() =>
    [...modules].sort((a, b) => (b.popularity || 0) - (a.popularity || 0)).slice(0, 8),
    [modules]
  );
  const featuredPaths = useMemo(() =>
    [...learningPaths].sort((a, b) => (b.popularity || 0) - (a.popularity || 0)).slice(0, 4),
    [learningPaths]
  );

  const handleSearch = (e) => {
    e.preventDefault();
    if (heroSearch.trim()) navigate(`/search?q=${encodeURIComponent(heroSearch.trim())}`);
  };

  return (
    <Box>
      {/* ── Hero ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          background: 'linear-gradient(160deg, #0d1117 0%, #0f2a4a 50%, #0d1117 100%)',
          color: '#fff',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute', inset: 0,
            background: 'radial-gradient(ellipse at 30% 50%, rgba(0,120,212,0.15) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(124,58,237,0.1) 0%, transparent 50%)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Stack alignItems="center" textAlign="center" spacing={4}>
            <Box>
              <Typography variant="overline" sx={{ color: '#0078d4', letterSpacing: 3, fontWeight: 700, fontSize: '0.75rem' }}>
                Microsoft Learn Catalog
              </Typography>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.2rem', md: '3.5rem', lg: '4rem' },
                  fontWeight: 800,
                  lineHeight: 1.1,
                  letterSpacing: '-2px',
                  mt: 1,
                }}
              >
                Build skills that{' '}
                <Box component="span" sx={{ color: '#0078d4' }}>
                  matter
                </Box>
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  maxWidth: 560,
                  mx: 'auto',
                  mt: 2,
                  lineHeight: 1.6,
                }}
              >
                Explore thousands of modules and learning paths from Microsoft Learn. 
                Build curated paths and track your progress.
              </Typography>
            </Box>

            {/* Search */}
            <Box component="form" onSubmit={handleSearch} sx={{ width: '100%', maxWidth: 600 }}>
              <Paper
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '14px',
                  px: 2,
                  py: 1,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  backgroundColor: 'rgba(255,255,255,0.07)',
                  backdropFilter: 'blur(12px)',
                }}
              >
                <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)', mr: 1.5, flexShrink: 0 }} />
                <InputBase
                  fullWidth
                  placeholder="Search modules, learning paths, topics..."
                  value={heroSearch}
                  onChange={(e) => setHeroSearch(e.target.value)}
                  sx={{ color: '#fff', fontSize: '1rem', '& input::placeholder': { color: 'rgba(255,255,255,0.4)' } }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ borderRadius: '10px', px: 2.5, py: 0.75, fontWeight: 600, flexShrink: 0 }}
                >
                  Search
                </Button>
              </Paper>
            </Box>

            {/* Quick topics */}
            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" sx={{ gap: 1 }}>
              {QUICK_TOPICS.map((t) => (
                <Chip
                  key={t.filter}
                  label={t.label}
                  clickable
                  onClick={() => navigate(`/search?q=${encodeURIComponent(t.label)}`)}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.8)',
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    '&:hover': { backgroundColor: 'rgba(0,120,212,0.3)', color: '#fff' },
                  }}
                />
              ))}
            </Stack>

            {/* Stats */}
            {!loading && modules.length > 0 && (
              <Stack direction="row" spacing={4} flexWrap="wrap" justifyContent="center" sx={{ gap: 3 }}>
                <StatBadge icon={School} label="Modules" value={modules.length.toLocaleString()} />
                <StatBadge icon={Route} label="Learning Paths" value={learningPaths.length.toLocaleString()} />
                <StatBadge icon={Star} label="Avg. Rating" value="4.8★" />
              </Stack>
            )}
          </Stack>
        </Container>
      </Box>

      {/* ── Content ────────────────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* Featured Modules */}
        <SectionHeader
          title="Trending Modules"
          subtitle="Most popular modules from Microsoft Learn"
          action={{ label: 'Browse all modules', href: '/modules' }}
        />
        {loading && modules.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2.5} sx={{ mb: 6 }}>
            {featured.map((m) => (
              <Grid key={m.uid} item xs={12} sm={6} md={4} lg={3}>
                <ContentCard item={m} variant="module" />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Featured Learning Paths */}
        <SectionHeader
          title="Featured Learning Paths"
          subtitle="Structured journeys to master new skills"
          action={{ label: 'All learning paths', href: '/learning-paths' }}
        />
        {loading && learningPaths.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            {featuredPaths.map((p) => (
              <Grid key={p.uid} item xs={12} sm={6} md={3}>
                <ContentCard item={p} variant="path" />
              </Grid>
            ))}
          </Grid>
        )}

        {/* CTA */}
        <Box
          sx={{
            mt: 8,
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #0078d4 0%, #005a9e 100%)',
            p: { xs: 4, md: 6 },
            color: '#fff',
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Build your own learning path
          </Typography>
          <Typography sx={{ opacity: 0.85, mb: 3, maxWidth: 480, mx: 'auto' }}>
            Curate modules into a custom learning path saved directly in your browser.
          </Typography>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowIcon />}
            onClick={() => navigate('/create-path')}
            sx={{
              backgroundColor: '#fff',
              color: '#0078d4',
              fontWeight: 700,
              borderRadius: '10px',
              px: 4,
              '&:hover': { backgroundColor: '#f0f4f8' },
            }}
          >
            Create a path
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

function SectionHeader({ title, subtitle, action }) {
  const navigate = useNavigate();
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} sx={{ mb: 3 }}>
      <Box>
        <Typography variant="h5" fontWeight={800} letterSpacing="-0.5px">{title}</Typography>
        {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{subtitle}</Typography>}
      </Box>
      {action && (
        <Button
          endIcon={<ArrowIcon />}
          onClick={() => navigate(action.href)}
          sx={{ fontWeight: 600, flexShrink: 0, mt: { xs: 1, sm: 0 } }}
        >
          {action.label}
        </Button>
      )}
    </Stack>
  );
}

function StatBadge({ icon: Icon, label, value }) {
  return (
    <Box textAlign="center">
      <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, lineHeight: 1 }}>{value}</Typography>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mt: 0.25 }}>
        <Icon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} />
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>{label}</Typography>
      </Stack>
    </Box>
  );
}
